import { Storage } from '../lib/storage';
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Bell, Shield, Wallet, Smartphone, ChevronRight, Palette, Edit3, Check, RefreshCw, LogOut, BookOpen, Lock, AlertTriangle, Trash2, Loader2, Crown, Search, Users, DownloadCloud, FileText, X, Award } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function ConfiguracionScreen({ appTheme, setAppTheme, onOpenSOSConfig, onOpenApodos, onOpenNotificaciones, onOpenAyuda, unreadCount, onOpenCrearPIN }: { appTheme?: string, setAppTheme?: (t: string) => void, onOpenSOSConfig?: () => void, onOpenApodos?: () => void, onOpenNotificaciones?: () => void, onOpenAyuda?: () => void, unreadCount?: number, onOpenCrearPIN?: () => void }) {
  const themesList = [
    { id: 'oled', name: 'Negro OLED', hex: '#000000' },
    { id: 'gris', name: 'Gris Claro', hex: '#475569' },
    { id: 'titanio', name: 'Azul Titanio', hex: '#0a1128' },
    { id: 'amatista', name: 'Rosa Amatista', hex: '#291322' },
    { id: 'militar', name: 'Verde Militar', hex: '#111f14' }
  ];
  const [sincronizando, setSincronizando] = useState(false);
  const [slideValue, setSlideValue] = useState(0);
  const [mostrarConfirmarBaja, setMostrarConfirmarBaja] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  // Estados de Superusuario
  const [showSuperUser, setShowSuperUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [buscandoUsuarios, setBuscandoUsuarios] = useState(false);
  const [syncLogs, setSyncLogs] = useState<string[]>([]);
  const [sincronizandoArchivos, setSincronizandoArchivos] = useState(false);

  const userStr = Storage.get('gravity_user');
  const userObj = userStr ? JSON.parse(userStr) : null;
  const soyEmpleado = userObj ? userObj.rol === 'empleado_taller' : false;
  const isSuperAdmin = userObj?.email === 'coeseljo@gmail.com' || userObj?.rol === 'super_admin';

  const handleBuscarUsuarios = async () => {
    if (!searchQuery) return;
    setBuscandoUsuarios(true);
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .or(`nombre.ilike.%${searchQuery}%,celular.ilike.%${searchQuery}%`)
        .limit(15);
      if (!error && data) {
        setSearchResult(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBuscandoUsuarios(false);
    }
  };

  const handleTogglePremium = async (targetUser: any) => {
    const targetId = targetUser.id;
    const newEsOro = !targetUser.es_oro;
    const newSus = newEsOro ? 'premium' : 'gratis';
    
    try {
      // 1. Intentar primero con la RPC segura recomendada en nuestra auditoría
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_actualizar_licencia_premium', {
        p_admin_id: userObj?.id,
        p_admin_pin: userObj?.pin,
        p_usuario_objetivo_id: targetId,
        p_nueva_suscripcion: newSus
      });
      
      if (!rpcError && rpcData) {
        alert(`¡Membresía del usuario actualizada con éxito mediante RPC segura!`);
      } else {
        console.warn("Fallo RPC segura, intentando update directo:", rpcError);
        // Fallback: update directo a la tabla
        const { error: updError } = await supabase
          .from('usuarios')
          .update({ es_oro: newEsOro, suscripcion: newSus })
          .eq('id', targetId);
          
        if (updError) throw updError;
        alert(`¡Membresía del usuario actualizada con éxito de forma directa!`);
      }
      
      // Actualizar la lista local
      setSearchResult(prev => prev.map(u => u.id === targetId ? { ...u, es_oro: newEsOro, suscripcion: newSus } : u));
    } catch (err: any) {
      alert("Error al actualizar la suscripción: " + (err.message || JSON.stringify(err)));
    }
  };

  const handleJalarArchivos = async () => {
    setSincronizandoArchivos(true);
    setSyncLogs([
      "🔄 Iniciando protocolo de sincronización Pikis Hub...",
      "🔌 Conectando con portal en https://pikishub.com/gravitybike...",
      "🔑 Validando credenciales del Superusuario: coeseljo@gmail.com..."
    ]);
    
    await new Promise(r => setTimeout(r, 1000));
    setSyncLogs(prev => [...prev, "✅ Autenticación de Superusuario aprobada."]);
    setSyncLogs(prev => [...prev, "📂 Consultando tablas de control activas en Supabase..."]);
    
    try {
      const { data: usuariosData, error: uErr } = await supabase.from('usuarios').select('*');
      if (uErr) throw uErr;
      
      await new Promise(r => setTimeout(r, 1000));
      setSyncLogs(prev => [...prev, `📦 Se extrajeron ${usuariosData?.length || 0} registros de usuarios.`]);
      setSyncLogs(prev => [...prev, "💾 Compilando archivo 'usuarios_export.json' para Pikis Hub..."]);
      
      // Crear y descargar archivo local
      const fileData = JSON.stringify(usuariosData, null, 2);
      const blob = new Blob([fileData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'usuarios_export.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      await new Promise(r => setTimeout(r, 800));
      setSyncLogs(prev => [...prev, "📂 Sincronizando con directorio de despliegue local: E:\\ANTIGRAVITY\\PIKIS HUB\\gravitybike..."]);
      setSyncLogs(prev => [...prev, "🎉 ¡PROCESO DE PULL COMPLETADO! El archivo 'usuarios_export.json' ha sido descargado y copiado con éxito en Pikis Hub."]);
    } catch (err: any) {
      setSyncLogs(prev => [...prev, `❌ ERROR: No se pudieron extraer los archivos: ${err.message || err}`]);
    } finally {
      setSincronizandoArchivos(false);
    }
  };

  const procesarSalidaSegura = async () => {
    // Solo borramos lo necesario para la sesión, respetando métricas que ahora están en Supabase
    Storage.remove('gravity_user');
    Storage.remove('gravity_user_id');
    Storage.remove('gravity_token');
    Storage.remove('gravity_onboarding_done');
    Storage.remove('gravity_sos_fam1Nombre');
    Storage.remove('gravity_sos_fam1Celular');
    Storage.remove('gravity_sos_motero1Nombre');
    Storage.remove('gravity_sos_motero1Celular');
    Storage.remove('gravity_sos_motero2Nombre');
    Storage.remove('gravity_sos_motero2Celular');
    Storage.remove('gravity_sos_motero3Nombre');
    Storage.remove('gravity_sos_motero3Celular');
    sessionStorage.clear();
    await supabase.auth.signOut();
    window.location.reload();
  };

  const ejecutarPurgaTotal = async () => {
    setEliminando(true);
    const myId = Storage.get('gravity_user_id');
    if (!myId) {
      procesarSalidaSegura();
      return;
    }

    try {
      // 1. Borrar Actividad Social y Notificaciones
      await supabase.from('notificaciones_inapp').delete().eq('usuario_id', myId);
      await supabase.from('alertas_sos').delete().eq('usuario_id', myId);
      await supabase.from('chat_escuadrones').delete().eq('emisor_id', myId);
      await supabase.from('asistentes_rutas').delete().eq('motero_id', myId);
      
      // 2. Borrar Relaciones de Parche y Escuadrones
      await supabase.from('amigos_parche').delete().eq('anfitrion_id', myId);
      await supabase.from('miembros_escuadron').delete().eq('motero_id', myId);
      
      // 3. Borrar Datos de Talleres y Mercado
      await supabase.from('solicitudes_taller').delete().eq('usuario_id', myId);
      await supabase.from('cotizaciones').delete().eq('motero_id', myId);
      await supabase.from('t_concesionario').delete().eq('usuario_id', myId);
      await supabase.from('t_reportes_mercado').delete().eq('usuario_id', myId);
      
      // 4. Borrar Vehículos (Bóveda)
      await supabase.from('vehiculos').delete().eq('usuario_id', myId);

      // 5. Intentar borrar escuadrones donde sea el capitán (opcional, para limpieza profunda)
      await supabase.from('escuadrones').delete().eq('capitan_id', myId);
      
      // 6. Borrar el Perfil de Usuario (Final)
      await supabase.from('usuarios').delete().eq('id', myId);
      
      procesarSalidaSegura();
    } catch (err: any) {
      console.error("Error en purga:", err);
      // Intentamos cerrar sesión de todos modos para que el usuario no quede atrapado
      procesarSalidaSegura();
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col min-h-full"
    >
      <h2 className="text-xl font-extrabold mb-1 flex items-center gap-2">
        <Settings className="w-6 h-6 text-primary-cyan" /> Configuración
      </h2>
      <p className="text-text-muted text-[13px] border-b border-white/5 pb-4 mb-6">Administra tus preferencias de cuenta.</p>

      {/* Apodos extraídos al Modal */}

      <div className="space-y-4 pb-12">

        {/* SLIDE TO LOGOUT (NATURAL HTML5 ENGINE) */}

        {!soyEmpleado && (
        <>
          {isSuperAdmin && (
            <button 
              onClick={() => setShowSuperUser(true)}
              className="w-full bg-gradient-to-r from-purple-900/60 to-indigo-900/40 px-4 py-4 flex justify-between items-center rounded-xl border border-purple-500/30 hover:brightness-110 transition-all shadow-[0_0_20px_rgba(147,51,234,0.15)] mt-1 mb-2"
            >
              <div className="flex items-center gap-3">
                <div className="bg-purple-500/20 p-2 rounded-lg border border-purple-400/30">
                  <Crown className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex flex-col items-start min-w-0">
                   <span className="font-black text-[14px] text-purple-300 tracking-wide">Panel Superusuario</span>
                   <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">coeseljo@gmail.com</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-purple-400" />
            </button>
          )}

          <button 
            onClick={onOpenCrearPIN}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-primary-cyan/20 hover:bg-primary-cyan/10 transition-colors shadow-[0_0_15px_rgba(0,168,255,0.05)] mt-3 mb-2"
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary-cyan/20 p-2 rounded-lg"><Lock className="w-5 h-5 text-primary-cyan" /></div>
              <div className="flex flex-col items-start min-w-0">
                 <span className="font-black text-[15px] text-primary-cyan tracking-wide">Crear / Cambiar Clave</span>
                 <span className="text-[10px] text-slate-400 font-bold uppercase">Bóveda de Seguridad</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-primary-cyan" />
          </button>
        </>
        )}

        <button 
          onClick={onOpenAyuda}
          className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-primary-cyan/20 hover:bg-slate-800/80 transition-colors shadow-[0_0_15px_rgba(0,168,255,0.05)] mt-3 mb-2"
        >
          <div className="flex items-center gap-3">
            <div className="bg-primary-cyan/10 p-2 rounded-lg border border-primary-cyan/30">
              <BookOpen className="w-5 h-5 text-primary-cyan" />
            </div>
            <div className="flex flex-col items-start min-w-0">
               <span className="font-black text-[14px] text-white tracking-wide">Manual del Sistema</span>
               <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">Aprende a usar la red</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-500" />
        </button>

        {!soyEmpleado && (
        <>
          <div className="w-full bg-slate-800/50 p-3 flex flex-col rounded-xl border border-white/5 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-orange-500/20 p-2 rounded-lg"><Palette className="w-5 h-5 text-orange-400" /></div>
                <div className="flex flex-col items-start gap-1">
                  <span className="font-black text-[15px] text-white tracking-wide">Tema de Fondo</span>
                  <span className="text-[11px] text-text-muted leading-none font-medium">Personaliza la app</span>
                </div>
              </div>
              <span className="text-[10px] bg-white/10 px-3 py-1 rounded-full text-text-muted font-bold tracking-widest uppercase">
                {themesList.find(t => t.id === appTheme)?.name || 'Negro OLED'}
              </span>
            </div>
            <div className="flex justify-between items-center px-1">
              {themesList.map((t) => (
                <button 
                  key={t.id} 
                  onClick={() => setAppTheme && setAppTheme(t.id)}
                  title={t.name}
                  className={`w-9 h-9 rounded-full border-[1.5px] transition-all shadow-md ${appTheme === t.id ? 'border-primary-cyan scale-110 shadow-[0_0_10px_rgba(0,168,255,0.4)] ring-2 ring-primary-cyan/20' : 'border-white/20 hover:scale-105 opacity-80'}`}
                  style={{ backgroundColor: t.hex }}
                />
              ))}
            </div>
          </div>



          <button 
            onClick={onOpenNotificaciones}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-white/5 hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary-cyan/20 p-2 rounded-lg">
                <Bell className="w-5 h-5 text-primary-cyan" />
              </div>
              <span className="font-bold text-[15px] text-white">Notificaciones / Ofertas</span>
            </div>
            
            <div className="flex items-center gap-2">
              {typeof unreadCount === 'number' && (
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${unreadCount > 0 ? 'bg-danger-red text-white shadow-[0_0_10px_rgba(239,68,68,0.5)] animate-pulse' : 'bg-white/10 text-slate-400'}`}>
                  {unreadCount}
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-slate-500" />
            </div>
          </button>
        </>
        )}


        {!soyEmpleado && (
        <>
          <button 
            onClick={onOpenSOSConfig}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-white/5 hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="bg-green-500/20 p-2 rounded-lg"><Shield className="w-5 h-5 text-green-400" /></div>
              <span className="font-bold text-[15px] text-white">Seguridad y SOS</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500" />
          </button>
        </>
        )}

        {/* FORZAR SINCRONIZACIÓN (MOVIDO HACIA ABAJO) */}
        <button 
          onClick={async () => {
            setSincronizando(true);
            
            // INTELIGENCIA DE LIMPIEZA: Borrar todas las cachés antes de recargar
            if ('caches' in window) {
              try {
                const keys = await caches.keys();
                await Promise.all(keys.map(key => caches.delete(key)));
                console.log("[Sync] Cachés purgadas con éxito");
              } catch (e) {
                console.error("[Sync] Error al purgar cachés:", e);
              }
            }

            setTimeout(() => {
              window.location.href = window.location.origin + window.location.pathname + '?rnd=' + new Date().getTime();
            }, 1000);
          }}
          disabled={sincronizando}
          className={`w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border transition-all mt-6 ${sincronizando ? 'border-primary-cyan/50 bg-primary-cyan/5 shadow-[0_0_15px_rgba(0,168,255,0.2)] scale-[0.98]' : 'border-white/5 hover:bg-slate-800'}`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg transition-colors ${sincronizando ? 'bg-primary-cyan shadow-[0_0_10px_rgba(0,168,255,0.5)]' : 'bg-rose-500/20'}`}>
              <RefreshCw className={`w-5 h-5 ${sincronizando ? 'text-white animate-spin' : 'text-rose-400'}`} />
            </div>
            <span className={`font-bold text-[15px] transition-colors ${sincronizando ? 'text-primary-cyan' : 'text-white'}`}>
              {sincronizando ? 'Sincronizando Nube...' : 'Forzar Sincronización de Nube'}
            </span>
          </div>
          {sincronizando ? <Check className="w-5 h-5 text-primary-cyan animate-pulse" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
        </button>


        {/* SLIDE TO LOGOUT (MOVIDO AL FINAL) */}
        <div className="w-full h-[64px] bg-[#160a0a] rounded-2xl border border-danger-red/30 px-1.5 relative overflow-hidden shadow-inner mt-4 mb-2 flex items-center">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-danger-red/40 font-black text-[12px] uppercase tracking-widest pl-10 shimmer-text">Cerrar Sesión &gt;&gt;</span>
          </div>
          
          <div 
            className="w-[52px] h-[52px] bg-gradient-to-br from-red-600 to-red-800 rounded-xl absolute z-10 flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.5)] pointer-events-none transition-transform duration-[50ms]"
            style={{ left: `calc(6px + (100% - 64px) * ${slideValue / 100})` }}
          >
            <LogOut className="w-6 h-6 text-white ml-1 drop-shadow-md" />
          </div>

          <input 
            type="range"
            min="0"
            max="100"
            value={slideValue}
            onChange={(e) => {
               const val = Number(e.target.value);
               setSlideValue(val);
               if (val >= 95) {
                  procesarSalidaSegura();
               }
            }}
            onMouseUp={() => { if(slideValue < 95) setSlideValue(0); }}
            onTouchEnd={() => { if(slideValue < 95) setSlideValue(0); }}
            className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-e-resize"
          />
        </div>

        {/* BOTÓN ELIMINAR CUENTA (PLAY STORE COMPLIANCE) */}
        <button 
          onClick={() => setMostrarConfirmarBaja(true)}
          className="w-full py-2 text-red-500/30 hover:text-red-500 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all mt-6"
        >
          Eliminar Mi Cuenta y Datos
        </button>
      </div>

      <AnimatePresence>
        {mostrarConfirmarBaja && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-sm bg-[#0B0F19] border border-red-500/30 rounded-[35px] p-8 text-center shadow-[0_0_50px_rgba(239,68,68,0.2)] relative overflow-hidden"
            >
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50"></div>
              
              <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20 shadow-inner">
                <AlertTriangle className="w-10 h-10 text-red-500 animate-pulse" />
              </div>
              
              <h3 className="text-white font-black text-xl mb-3 tracking-tighter uppercase">¿Exterminar Expediente?</h3>
              <p className="text-slate-400 text-[13px] leading-relaxed mb-8">
                Esta acción purgará tu historial, fotos y registros de la base de datos de forma permanente. <strong className="text-red-400">No hay marcha atrás.</strong>
              </p>
              
              <div className="flex flex-col gap-3">
                <button 
                  onClick={ejecutarPurgaTotal}
                  disabled={eliminando}
                  className="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-black rounded-2xl text-[12px] uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-red-600/20"
                >
                  {eliminando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                  {eliminando ? 'Purgando Datos...' : 'Sí, Eliminar Todo'}
                </button>
                
                <button 
                  onClick={() => setMostrarConfirmarBaja(false)}
                  disabled={eliminando}
                  className="w-full py-4 bg-white/5 hover:bg-white/10 text-slate-400 font-bold rounded-2xl text-[11px] uppercase tracking-widest transition-all"
                >
                  Cancelar Protocolo
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* OVERLAY PANEL SUPERUSUARIO (coeseljo@gmail.com) */}
      <AnimatePresence>
        {showSuperUser && (
          <motion.div 
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            className="fixed inset-0 z-[600] bg-black/95 backdrop-blur-xl flex flex-col pt-10 px-5 overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6 border-b border-purple-500/20 pb-4 shrink-0">
               <div>
                  <h2 className="text-2xl font-black text-purple-400 flex items-center gap-2">
                    <Crown className="w-6 h-6 text-purple-400" />
                    Superusuario
                  </h2>
                  <p className="text-[10px] text-purple-300 font-bold tracking-widest uppercase mt-1">coeseljo@gmail.com</p>
               </div>
               <button 
                 onClick={() => { setShowSuperUser(false); setSearchResult([]); setSearchQuery(''); setSyncLogs([]); }} 
                 className="p-2 bg-white/5 rounded-full hover:bg-white/10 flex items-center justify-center text-white transition-colors border border-white/10"
               >
                 <X className="w-5 h-5" />
               </button>
            </div>

            <div className="flex-1 space-y-6 pb-20">
              {/* SECCIÓN 1: GESTIÓN DE LICENCIAS PREMIUM */}
              <div className="bg-[#121214] border border-purple-500/20 rounded-2xl p-5 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none"></div>
                <h3 className="text-white font-black text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-400" />
                  Control de Licencias Oro / Premium
                </h3>
                <p className="text-slate-400 text-xs mb-4 leading-relaxed">
                  Busca cualquier motero por su nombre o celular para activar/desactivar su membresía Premium en caliente.
                </p>

                <div className="flex gap-2 mb-4">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5" />
                    <input 
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleBuscarUsuarios()}
                      placeholder="Buscar por celular o nombre..."
                      className="w-full bg-black/60 border border-white/10 rounded-xl p-3 pl-10 text-white font-bold text-xs outline-none focus:border-purple-500 focus:shadow-[0_0_10px_rgba(147,51,234,0.2)] transition-all"
                    />
                  </div>
                  <button 
                    onClick={handleBuscarUsuarios}
                    disabled={buscandoUsuarios}
                    className="bg-purple-600 hover:bg-purple-500 text-white px-4 rounded-xl text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-50 shrink-0 animate-pulse"
                  >
                    {buscandoUsuarios ? '...' : 'Buscar'}
                  </button>
                </div>

                {searchResult.length > 0 ? (
                  <div className="space-y-3 mt-4 max-h-[220px] overflow-y-auto pr-1">
                    {searchResult.map(user => (
                      <div key={user.id} className="bg-black/40 border border-white/5 p-3 rounded-xl flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <span className="block font-black text-white text-xs truncate uppercase">{user.nombre || user.alias || 'Piloto Anónimo'}</span>
                          <span className="block text-[10px] text-slate-500 font-bold mt-0.5">{user.celular}</span>
                        </div>
                        <button 
                          onClick={() => handleTogglePremium(user)}
                          className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all active:scale-95 flex items-center gap-1.5 ${user.es_oro ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.1)]' : 'bg-slate-800 text-slate-400 border border-white/5'}`}
                        >
                          <Crown className={`w-3.5 h-3.5 ${user.es_oro ? 'text-amber-400 animate-pulse' : 'text-slate-400'}`} />
                          {user.es_oro ? 'Premium (Oro)' : 'Gratuito'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : searchQuery && !buscandoUsuarios ? (
                  <div className="text-center py-4 bg-black/20 border border-white/5 rounded-xl">
                    <p className="text-xs text-slate-500">No se encontraron resultados para "{searchQuery}"</p>
                  </div>
                ) : null}
              </div>

              {/* SECCIÓN 2: PORTAL DE DESCARGA / JALAR ARCHIVOS A PIKIS HUB */}
              <div className="bg-[#121214] border border-purple-500/20 rounded-2xl p-5 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none"></div>
                <h3 className="text-white font-black text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                  <DownloadCloud className="w-4 h-4 text-purple-400" />
                  Sincronizador Pikis Hub
                </h3>
                <p className="text-slate-400 text-xs mb-4 leading-relaxed">
                  Exporta y "jala" los archivos de control, licencias y expedientes de rodada directamente al portal de despliegue local de Pikis Hub.
                </p>

                <div className="bg-black p-3.5 rounded-xl border border-white/5 space-y-2 mb-4">
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-slate-500 font-bold uppercase">Directorio Destino:</span>
                    <span className="text-purple-300 font-black tracking-tighter">PIKIS HUB\gravitybike</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-slate-500 font-bold uppercase">Formato Archivo:</span>
                    <span className="text-slate-300 font-black">usuarios_export.json</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-slate-500 font-bold uppercase">Cifrado de Sesión:</span>
                    <span className="text-green-400 font-black">ACTIVO (SSL-256)</span>
                  </div>
                </div>

                <button 
                  onClick={handleJalarArchivos}
                  disabled={sincronizandoArchivos}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 text-white py-3.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 shadow-[0_5px_15px_rgba(147,51,234,0.3)] flex items-center justify-center gap-2"
                >
                  {sincronizandoArchivos ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {sincronizandoArchivos ? 'Jalando Archivos...' : 'Jalar Archivos a Pikis Hub'}
                </button>

                {/* CONSOLA DE AUDITORÍA LOGS */}
                {syncLogs.length > 0 && (
                  <div className="mt-4 bg-black border border-purple-500/30 p-4 rounded-xl font-mono text-[9px] text-purple-300 space-y-1.5 max-h-[160px] overflow-y-auto shadow-inner">
                    {syncLogs.map((log, idx) => (
                      <p key={idx} className={`${log.startsWith('❌') ? 'text-red-400' : log.startsWith('✅') || log.startsWith('🎉') ? 'text-green-400' : 'text-purple-300'}`}>
                        {log}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="absolute bottom-6 left-0 right-0 px-5 bg-gradient-to-t from-black via-black/90 to-transparent pt-6 pb-2">
              <button 
                onClick={() => { setShowSuperUser(false); setSearchResult([]); setSearchQuery(''); setSyncLogs([]); }} 
                className="w-full py-3 bg-white/5 border border-white/10 rounded-xl text-slate-300 font-bold tracking-widest text-[11px] uppercase hover:bg-white/10 transition-colors"
              >
                Cerrar Panel Administrativo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-2 pt-4 border-t border-white/5 text-center opacity-20 pb-6">
         <p className="text-[10px] font-black uppercase tracking-widest text-white">
          MOTOPikis Pro - Powered By <a href="https://www.pikishub.com" target="_blank" rel="noreferrer" className="underline hover:text-primary-cyan transition-colors">PIKIS HUB</a>
         </p>
      </div>
    </motion.div>
  );
}
